import { useState } from "react";
import { IconFolder, IconFolderOpen, IconChevronRight, IconChevronDown } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { TableauProject, ProjectNode } from "./types";

interface ProjectTreeItemProps {
  project: ProjectNode;
  selectedProject: TableauProject | null;
  onSelectProject: (project: TableauProject) => void;
  disabledProjectIds?: Set<string>;
  /** Tooltip shown on disabled tree items. Defaults to the Creator-flow copy
   * ("already contains an altimate_workbooks subproject"). Pass the Explorer
   * variant when the picker is in "locate" mode. */
  disabledTooltip?: string;
  level: number;
  renderChildren: (children: ProjectNode[]) => React.ReactNode;
}

export default function ProjectTreeItem({
  project,
  selectedProject,
  onSelectProject,
  disabledProjectIds,
  disabledTooltip,
  renderChildren,
}: ProjectTreeItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = project.children.length > 0;
  const isSelected = selectedProject?.id === project.id;
  const isDisabled = disabledProjectIds?.has(project.id) || false;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleSelect = () => {
    if (!isDisabled) {
      onSelectProject(project);
    }
  };

  return (
    <div>
      {/* Project Item */}
      <div
        className={cn(
          "al-group al-flex al-items-center al-gap-2 al-py-2 al-px-3 al-rounded-md al-transition-colors",
          isDisabled
            ? "al-bg-gray-50 al-text-gray-400 al-cursor-not-allowed al-opacity-60"
            : isSelected
              ? "al-bg-blue-100 al-text-blue-900 al-cursor-pointer"
              : "hover:al-bg-gray-100 al-text-gray-700 al-cursor-pointer"
        )}
        onClick={handleSelect}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelect();
          }
        }}
        title={
          isDisabled
            ? (disabledTooltip ?? "This project already contains an altimate_workbooks subproject")
            : undefined
        }
      >
        {/* Expand/Collapse Button */}
        <button
          onClick={handleToggle}
          className={cn(
            "al-p-0.5 al-rounded al-transition-colors",
            hasChildren ? "hover:al-bg-gray-200" : "al-invisible"
          )}
          aria-label={isExpanded ? "Collapse" : "Expand"}
          disabled={!hasChildren}
          type="button"
        >
          {hasChildren ? (
            isExpanded ? (
              <IconChevronDown className="al-w-4 al-h-4" />
            ) : (
              <IconChevronRight className="al-w-4 al-h-4" />
            )
          ) : (
            <div className="al-w-4 al-h-4" />
          )}
        </button>

        {/* Folder Icon */}
        {isExpanded && hasChildren ? (
          <IconFolderOpen className="al-w-5 al-h-5 al-text-yellow-600 al-flex-shrink-0" />
        ) : (
          <IconFolder className="al-w-5 al-h-5 al-text-yellow-600 al-flex-shrink-0" />
        )}

        {/* Project Name */}
        <span className="al-flex-1 al-text-sm al-font-medium al-truncate">{project.name}</span>

        {/* Item Count Badge (if has children) */}
        {hasChildren && (
          <span className="al-text-xs al-px-2 al-py-0.5 al-rounded-full al-bg-gray-200 group-hover:al-bg-gray-300 al-text-gray-600">
            {project.children.length}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="al-ml-4 al-border-l al-border-gray-200 al-pl-2 al-mt-1">
          {renderChildren(project.children)}
        </div>
      )}
    </div>
  );
}
